import React, { JSX, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import {
  getContentMetadataData,
  IContentMetadata,
  IContentMetadataQueryParams,
} from '~/src/dash/apiV2/inventory-rules/content-metadata';
import { getInventoryRulesFilters } from '~/src/dash/apiV2/inventory-rules/inventoryRules';
import { ISelectOption } from '~/src/dash/components/dropdowns/common';
import { formatSelectedFiltersIntoQueryObject } from '~/src/dash/components/filters/conversions';
import { SearchResultsText } from '~/src/dash/components/input/common';
import { formatDataIntoLVTRows } from '~/src/dash/components/tables/IDNameTable';
import { ListViewTable } from '~/src/dash/components/tables/ListViewTable';
import {
  extractDefaultActiveColumnKeys,
  extractVisibleColumns,
  IListViewTableColumnInfo,
  Order,
  RowsPerPage,
} from '~/src/dash/components/tables/ListViewTable/common';
import { ColumnSelector } from '~/src/dash/components/tables/ListViewTable/linked_components/ColumnSelector';
import {
  Col,
  Cols,
  Panel,
  PanelBreak,
  Panels,
  Row,
  Rows,
  spacing,
  TextInput,
  usePublicaTranslation,
} from '~/src/dash/publicaUI';
import {
  LoadingState,
  LoadingStateHasData,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import { InventoryRulesModal } from '~/src/dash/views/inventory-rules-v2/InventoryRulesModal/InventoryRulesModal';
import { contentMetadataListConfig } from '~/src/dash/views/inventory-rules-v2/ContentMetadata/config';
import { Filters } from '~/src/dash/views/inventory-rules-v2/Rules/InventoryRulesList/Filters';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

export const ContentMetadataTable = (): JSX.Element => {
  const { isSuperUser, session } = useBaseStore();
  const { t } = usePublicaTranslation();
  const cfg = contentMetadataListConfig;
  const [activeColumnKeys, setActiveColumnKeys] = useState<string[]>(
    cfg.activeColumnKeys || extractDefaultActiveColumnKeys(cfg.columns),
  );

  // filters
  const [filtersLoadState, setFiltersLoadState] = useState<LoadingState>(LoadingStateLoading);
  const [filters, setFilters] = useState<Record<string, ISelectOption[]>>({});
  const [selectedFilters, setSelectedFilters] = useState<Record<string, Set<string | number>>>({});
  // table
  const [loadState, setLoadState] = useState<LoadingState>(LoadingStateLoading);
  const [responseData, setResponseData] = useState<IContentMetadata[]>([]);
  const [totalRowCount, setTotalRowCount] = useState(0);

  const [query, setQuery] = useState('');

  const [order, setOrder] = useState<Order>(cfg.defaultState.orderDirection || 'asc');
  const [orderBy, setOrderBy] = useState<string>((cfg.defaultState.orderBy as string) || 'ID');
  const [page, setPage] = useState<number>(cfg.defaultState.page || 0);
  const [rowsPerPage, setRowsPerPage] = useState(cfg.defaultState.rowsPerPage || 10);
  const tableAbortControllerRef = useRef<AbortController | null>(null);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarError, setSnackbarError] = useState(false);
  const [activeContentMetadata, setActiveContentMetadata] = useState<IContentMetadata | undefined>(
    undefined,
  );
  const [isInventoryRulesModalActive, setIsInventoryRulesModalActive] = useState(false);

  const isMounted = useRef(false);

  const handleOnChangeQuery = (text: string): void => {
    unstable_batchedUpdates(() => {
      setQuery(text);
      setPage(0);
    });
  };

  const handleFilterAccept = (value: Set<number | string>, key: string): void => {
    if (
      selectedFilters[key].size === value.size &&
      [...selectedFilters[key]].every((item) => value.has(item))
    ) {
      return;
    }

    const clonedSelectedFilters = { ...selectedFilters };
    clonedSelectedFilters[key] = value;
    unstable_batchedUpdates(() => {
      setSelectedFilters(clonedSelectedFilters);
      setPage(0);
    });
    cfg.filters[key].selectedOptionIDs = value;
    cfg.table.page = 0;
  };

  const handleClearFilters = (): void => {
    const clearedSelectedFilters: Record<string, Set<string | number>> = {};
    Object.values(cfg.filters).forEach((filter) => {
      const initialDefaultIDs = new Set<string | number>(filter.selectedOptionIDsOnReset || []);
      clearedSelectedFilters[filter.key] = initialDefaultIDs;
      cfg.filters[filter.key].selectedOptionIDs = initialDefaultIDs;
    });
    unstable_batchedUpdates(() => {
      setSelectedFilters(clearedSelectedFilters);
      setPage(0);
    });
    cfg.table.page = 0;
  };

  const handleOnClickColumnHeader = (column: IListViewTableColumnInfo): void => {
    const { orderByKey, requestKey, defaultOrderDirection } = column;
    const key = orderByKey || requestKey;
    if (orderBy !== key) {
      unstable_batchedUpdates(() => {
        setOrder(defaultOrderDirection || 'asc');
        setOrderBy(key);
        setPage(0);
      });
    } else {
      const isAsc = orderBy === key && order === 'asc';
      unstable_batchedUpdates(() => {
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(key);
        setPage(0);
      });
    }
  };

  const handleChangePage = (_e: unknown, newPage: number): void => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const updatedRowsPerPage = Number(event.target.value) as RowsPerPage;
    unstable_batchedUpdates(() => {
      setRowsPerPage(updatedRowsPerPage);
      setPage(0);
    });
  };

  const handleChangeActiveColumns = (keys: string[]): void => {
    setActiveColumnKeys(keys);
  };

  const handleAcceptActiveColumns = (keys: string[]): void => {
    setActiveColumnKeys(keys);
    cfg.activeColumnKeys = keys;
  };

  const handleClickOnInventoryRulesList = (id: string): void => {
    const selectedContentMetadata = responseData.find(
      (contentMetadata: IContentMetadata) => contentMetadata.ID.toString() === id.toString(),
    );
    unstable_batchedUpdates(() => {
      setActiveContentMetadata(selectedContentMetadata);
      setIsInventoryRulesModalActive(true);
    });
  };

  const handleCloseModals = (): void => {
    unstable_batchedUpdates(() => {
      setActiveContentMetadata(undefined);
      setIsInventoryRulesModalActive(false);
    });
  };

  const populateFilters = (): void => {
    setFiltersLoadState(LoadingStateLoading);
    const tempFilters: Record<string, ISelectOption[]> = {};
    const tempSelectedFilters: Record<string, Set<string | number>> = {};

    getInventoryRulesFilters(session.publisher.ID)
      .then((inventoryRulesFilters) => {
        Object.keys(cfg.filters).forEach((filterKey) => {
          tempFilters[filterKey] = inventoryRulesFilters[filterKey] || [];
          tempSelectedFilters[filterKey] =
            cfg.filters[filterKey].selectedOptionIDs || new Set<string | number>();
        });

        unstable_batchedUpdates(() => {
          setFilters(tempFilters);
          setSelectedFilters(tempSelectedFilters);
          setFiltersLoadState(LoadingStateHasData);
        });
      })
      .catch((err) => {
        console.error(err);
        unstable_batchedUpdates(() => {
          setFilters({});
          setSelectedFilters({});
          setFiltersLoadState(LoadingStateNoData);
        });
      });
  };

  const fetchTableData = useCallback((): void => {
    if (tableAbortControllerRef.current) {
      tableAbortControllerRef.current.abort();
    }

    tableAbortControllerRef.current = new AbortController();
    const currentController = tableAbortControllerRef.current;

    setLoadState(LoadingStateLoading);

    const filterParams = formatSelectedFiltersIntoQueryObject(selectedFilters, cfg.filters);

    const tableQuery = {
      Search: query,
      Page: page,
      PageSize: rowsPerPage,
      OrderBy: orderBy,
      OrderDirection: order,
      ...filterParams,
    } as IContentMetadataQueryParams;

    getContentMetadataData(session.publisher.ID, tableQuery, currentController.signal)
      .then((response) => {
        unstable_batchedUpdates(() => {
          setLoadState(LoadingStateHasData);
          setResponseData(response.InvRuleContentData || []);
          setTotalRowCount(response.TotalCount || 0);
        });
      })
      .catch((err: Error) => {
        if (err.name === 'AbortError') {
          return;
        }
        unstable_batchedUpdates(() => {
          setLoadState(LoadingStateNoData);
          setResponseData([]);
          setTotalRowCount(0);
        });
        console.error(err);
      });
  }, [session.publisher.ID, order, orderBy, rowsPerPage, page, query, selectedFilters]);

  useEffect(() => {
    populateFilters();
  }, []);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      if (tableAbortControllerRef.current) {
        tableAbortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    if (isMounted.current) {
      fetchTableData();
    }
  }, [fetchTableData]);

  const activeColumnsList = useMemo(
    () => extractVisibleColumns(cfg.columns, activeColumnKeys, isSuperUser),
    [],
  );

  const rows = useMemo(
    () => formatDataIntoLVTRows(responseData, cfg.columns),
    [responseData, cfg.columns],
  );

  return (
    <Panels
      max
      vertical
      relative
      largeHorizontalPadding
      verticalPadding
      style={{ rowGap: spacing.medium }}
    >
      <Panel highlighted>
        <Rows>
          <Row noVerticalPadding>
            <Filters
              cypressParentName={cfg.cypressClassName}
              labelWidth={96}
              labelHeight={36}
              enableClear={true}
              filtersConfig={cfg.filters}
              filtersToOptionsMap={filters}
              handleClearFilters={handleClearFilters}
              handleApplySelect={handleFilterAccept}
              selectedFilters={selectedFilters}
              loadState={filtersLoadState}
            />
          </Row>
        </Rows>
      </Panel>
      <Panel highlighted basePadding>
        <Rows>
          <Row noPadding>
            <Cols
              wrap
              gap={spacing.medium}
              style={{
                alignItems: 'center',
                rowGap: spacing.small,
                justifyContent: 'space-between',
              }}
            >
              <Cols gap={spacing.medium} style={{ alignItems: 'center', rowGap: spacing.small }}>
                <Col>
                  <TextInput
                    enableClear
                    handleOnChange={handleOnChangeQuery}
                    msDelay={500}
                    placeholder={t('SearchByName')}
                    prefix='search'
                  />
                </Col>
                <Col>
                  <SearchResultsText
                    numRowsDisplayed={responseData.length}
                    numTotalRows={totalRowCount}
                  />
                </Col>
              </Cols>
              <Col>
                <ColumnSelector
                  preselectedColumnKeys={activeColumnKeys}
                  resetActiveColumnKeys={cfg.columnSelector.resetKeys}
                  columnsList={activeColumnsList}
                  columnsHandleOnChangeOption={handleChangeActiveColumns}
                  columnsHandleOnAccept={handleAcceptActiveColumns}
                />
              </Col>
            </Cols>
          </Row>
          <Row noHorizontalPadding verticalPadding>
            <ListViewTable
              activeColumnKeys={activeColumnKeys}
              columns={cfg.columns}
              customFunctions={{
                handleClickOnInventoryRulesList,
              }}
              displayNameWithID
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              handleOnClickColumnHeader={handleOnClickColumnHeader}
              isReadAuthorized={true}
              isSuperUser={isSuperUser}
              isWriteAuthorized={true}
              loadState={loadState}
              order={order}
              orderBy={orderBy}
              page={page}
              rows={rows}
              rowsPerPage={rowsPerPage}
              tableName={t('ContentMetadata')}
              totalRowCount={totalRowCount}
              className={cfg.cypressClassName}
            />
          </Row>
        </Rows>
      </Panel>
      <InventoryRulesModal
        open={isInventoryRulesModalActive}
        onClose={handleCloseModals}
        inventoryRules={activeContentMetadata?.InventoryRules ?? null}
        descriptionTKey='InventoryRulesContentMetadataModalDescription'
      />
      <SnackbarView
        open={!!snackbarMessage}
        error={!!snackbarError}
        onClose={() => {
          unstable_batchedUpdates(() => {
            setSnackbarMessage('');
            setSnackbarError(false);
          });
        }}
      >
        {snackbarMessage}
      </SnackbarView>
      <PanelBreak height={spacing.large} />
    </Panels>
  );
};
